/**
 * React Hooks for Signal Data
 */

import { useCallback, useEffect, useState } from 'react';
import { signalRepository } from '../repositories/SignalRepository';
import {
    EntityFilterOptions,
    EntitySortOptions,
    Signal
} from '../types/WorkflowEntities';

// Hook for fetching all signals
export function useSignals(options?: {
    filter?: EntityFilterOptions;
    sort?: EntitySortOptions;
    autoRefresh?: boolean;
    refreshInterval?: number;
}) {
    const [signals, setSignals] = useState<Signal[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchSignals = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await signalRepository.findAll({
                filter: options?.filter as any,
                sort: options?.sort as any
            });
            setSignals(data);
        } catch (err) {
            setError(err as Error);
            setSignals([]);
        } finally {
            setLoading(false);
        }
    }, [options?.filter, options?.sort]);

    useEffect(() => {
        fetchSignals();

        if (options?.autoRefresh && options?.refreshInterval) {
            const interval = setInterval(fetchSignals, options.refreshInterval);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [fetchSignals, options?.autoRefresh, options?.refreshInterval]);

    return {
        signals,
        loading,
        error,
        refetch: fetchSignals
    };
}

// Hook for fetching a single signal
export function useSignal(signalId: string | null) {
    const [signal, setSignal] = useState<Signal | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchSignal = useCallback(async () => {
        if (!signalId) {
            setSignal(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await signalRepository.findById(signalId);
            setSignal(data);
        } catch (err) {
            setError(err as Error);
            setSignal(null);
        } finally {
            setLoading(false);
        }
    }, [signalId]);

    useEffect(() => {
        fetchSignal();
    }, [fetchSignal]);

    return {
        signal,
        loading,
        error,
        refetch: fetchSignal
    };
}

// Hook for signal CRUD operations
export function useSignalMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const createSignal = useCallback(async (data: Partial<Signal>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await signalRepository.create(data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateSignal = useCallback(async (id: string, data: Partial<Signal>) => {
        setLoading(true);
        setError(null);

        try {
            const result = await signalRepository.update(id, data);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteSignal = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await signalRepository.delete(id);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const sendSignal = useCallback(async (input: { instanceId: string; signalName: string; payload?: any; metadata?: any; }) => {
        setLoading(true);
        setError(null);

        try {
            const result = await signalRepository.send(input);
            return result;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        createSignal,
        updateSignal,
        deleteSignal,
        sendSignal,
        loading,
        error
    };
}

// Hook for signal search
export function useSignalSearch(query: string, debounceMs: number = 300) {
    const [results, setResults] = useState<Signal[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let cleanup: (() => void) | undefined;
        if (!query || query.trim() === '') {
            setResults([]);
        } else {
            const timer = setTimeout(async () => {
                setLoading(true);
                setError(null);

                try {
                    const data = await signalRepository.search(query);
                    setResults(data);
                } catch (err) {
                    setError(err as Error);
                    setResults([]);
                } finally {
                    setLoading(false);
                }
            }, debounceMs);
            cleanup = () => clearTimeout(timer);
        }
        return cleanup;
    }, [query, debounceMs]);

    return {
        results,
        loading,
        error
    };
}

export default {
    useSignals,
    useSignal,
    useSignalMutations,
    useSignalSearch
};